import { useEffect } from 'react';

/**
 * Manages the <meta name="robots"> tag for the current route.
 *
 * Passing a policy like "noindex, nofollow" upserts the tag; on unmount we
 * remove it (or restore the previous value) so we never leak a per-route
 * directive to the next page. Static HTML shell defaults to "index, follow",
 * so anything that DOESN'T call this hook stays crawlable.
 *
 * Also attaches an X-Robots-Tag equivalent via a `data-x-robots-tag` attr
 * on <html> — purely a debug marker (real X-Robots-Tag is set in vercel.json
 * headers), so QA can inspect via DevTools.
 *
 * @param {string} policy - e.g. "noindex, nofollow"
 * @param {boolean} enabled - allow policy to be toggled off (LAUNCH_MODE-aware
 *   surfaces pass false when the surface becomes indexable)
 */
export const useRobotsMeta = (policy, enabled = true) => {
  useEffect(() => {
    if (!enabled || !policy) return undefined;

    const selector = 'meta[name="robots"]';
    let el = document.head.querySelector(selector);
    const created = !el;
    const previous = el ? el.getAttribute('content') : null;

    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', 'robots');
      document.head.appendChild(el);
    }
    el.setAttribute('content', policy);
    document.documentElement.setAttribute('data-x-robots-tag', policy);

    return () => {
      if (created && el.parentNode) {
        el.parentNode.removeChild(el);
      } else if (previous !== null && el) {
        el.setAttribute('content', previous);
      }
      document.documentElement.removeAttribute('data-x-robots-tag');
    };
  }, [policy, enabled]);
};

export default useRobotsMeta;
